'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import Badge from '@/components/shared/Badge';
import RestrictedState from '@/components/shared/RestrictedState';
import { Skeleton } from '@/components/shared/Skeletons';
import { mockListingsRE, getREListingBySlug, RealEstateListing } from '@/data/mockRealEstate';
import { ArrowLeft, Home, Info } from 'lucide-react';

function getStatusBadgeVariant(status: string) {
  if (status === 'Active' || status === 'Available') return 'available' as const;
  if (status === 'Pending') return 'pendingSale' as const;
  return 'sold' as const;
}

function DetailSkeleton() {
  return (
    <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-10">
      <Skeleton className="h-4 w-32 mb-8" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <Skeleton className="aspect-square rounded-xl" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-24 rounded-full" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
          <Skeleton className="h-3 w-3/5" />
        </div>
      </div>
    </div>
  );
}

function RelatedListingCard({ listing }: { listing: RealEstateListing }) {
  const color = listing.propertyType === 'Townhome' ? '#F0A832' : listing.propertyType === 'Tower 1' ? '#4CC9FF' : '#9B7FFF';
  return (
    <Link href={`/resources/real-estate/${listing.slug}`} className="block group focus-visible:outline-none">
      <div className="neon-card p-4 group-focus-visible:border-[rgba(76,201,255,0.35)]">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="font-semibold text-[13px] leading-snug" style={{ color: '#F2F4FF' }}>
            {listing.unitLabel}
          </h4>
          <Badge variant={getStatusBadgeVariant(listing.status)} label={listing.status} />
        </div>
        <p className="font-display font-bold text-[16px] mb-1" style={{ color }}>
          {listing.priceDisplay}
        </p>
        <p className="text-[12px]" style={{ color: '#A7A3C4' }}>{listing.summary}</p>
      </div>
    </Link>
  );
}

export default function RealEstateDetailPage() {
  const params = useParams();
  const slug = params?.listingOrGuideSlug as string;
  const [isLoading, setIsLoading] = useState(true);
  const [listing, setListing] = useState<RealEstateListing | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const found = getREListingBySlug(slug);
      if (found) {
        setListing(found);
      } else {
        setNotFound(true);
      }
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [slug]);

  const relatedListings = listing
    ? mockListingsRE.filter((l) => l.slug !== slug && l.propertyType === listing.propertyType).slice(0, 3)
    : [];

  const color = listing
    ? listing.propertyType === 'Townhome' ? '#F0A832' : listing.propertyType === 'Tower 1' ? '#4CC9FF' : '#9B7FFF' :'#4CC9FF';

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {isLoading && <DetailSkeleton />}

        {!isLoading && notFound && (
          <div className="max-w-2xl mx-auto px-6 py-20">
            <Link href="/resources/real-estate" className="inline-flex items-center gap-2 text-[13px] mb-8 hover:opacity-80" style={{ color: '#A7A3C4' }}>
              <ArrowLeft size={14} /> Back to Real Estate
            </Link>
            <RestrictedState
              variant="removed"
              title="Listing Unavailable"
              description="This listing has been removed or is no longer available. Browse current listings for other options."
            />
          </div>
        )}

        {!isLoading && listing && (
          <>
            {/* Back nav */}
            <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-4">
                <Link href="/resources/real-estate" className="inline-flex items-center gap-2 text-[13px] hover:opacity-80" style={{ color: '#A7A3C4' }}>
                  <ArrowLeft size={14} /> Back to Real Estate
                </Link>
              </div>
            </div>

            <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-10">
              {/* Disclaimer */}
              <div
                className="px-4 py-3 rounded-lg mb-8 flex items-start gap-2"
                style={{ background: 'rgba(240,168,50,0.04)', border: '1px solid rgba(240,168,50,0.12)' }}
              >
                <Info size={13} style={{ color: '#F0A832', marginTop: 1, flexShrink: 0 }} />
                <p className="text-[12px]" style={{ color: 'rgba(167,163,196,0.60)' }}>
                  Mocked listing for demonstration only. Not a real MLS listing.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Gallery placeholder */}
                <div>
                  <div
                    className="rounded-xl flex items-center justify-center aspect-square mb-3"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <div className="text-center">
                      <Home size={48} style={{ color: 'rgba(167,163,196,0.15)', margin: '0 auto 10px' }} />
                      <p className="text-[13px]" style={{ color: 'rgba(167,163,196,0.30)' }}>{listing.propertyType}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="w-16 h-16 rounded-lg flex items-center justify-center"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                      >
                        <Home size={14} style={{ color: 'rgba(167,163,196,0.15)' }} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span
                        className="text-[11px] px-2.5 py-1 rounded font-medium"
                        style={{ background: `${color}10`, color, border: `1px solid ${color}20` }}
                      >
                        {listing.propertyType}
                      </span>
                      {listing.isRental && (
                        <span
                          className="text-[11px] px-2.5 py-1 rounded font-medium"
                          style={{ background: 'rgba(45,212,160,0.10)', color: '#2DD4A0', border: '1px solid rgba(45,212,160,0.20)' }}
                        >
                          Rental
                        </span>
                      )}
                      <Badge variant={getStatusBadgeVariant(listing.status)} label={listing.status} size="md" />
                    </div>
                    <h1 className="font-display font-bold text-[24px] sm:text-[28px] leading-tight mb-2" style={{ color: '#F2F4FF' }}>
                      {listing.unitLabel}
                    </h1>
                    <div className="font-display font-bold text-[32px] mb-1" style={{ color: '#F2F4FF' }}>
                      {listing.priceDisplay}
                    </div>
                    {listing.summary && (
                      <p className="text-[13px]" style={{ color: '#A7A3C4' }}>{listing.summary}</p>
                    )}
                  </div>

                  {/* Stats */}
                  {(listing.beds || listing.baths || listing.sqft) && (
                    <div className="grid grid-cols-3 gap-3">
                      {listing.beds && (
                        <div className="neon-card p-3 text-center">
                          <div className="font-display font-bold text-[18px]" style={{ color: '#F2F4FF' }}>{listing.beds}</div>
                          <div className="text-[11px]" style={{ color: '#A7A3C4' }}>Beds</div>
                        </div>
                      )}
                      {listing.baths && (
                        <div className="neon-card p-3 text-center">
                          <div className="font-display font-bold text-[18px]" style={{ color: '#F2F4FF' }}>{listing.baths}</div>
                          <div className="text-[11px]" style={{ color: '#A7A3C4' }}>Baths</div>
                        </div>
                      )}
                      {listing.sqft && (
                        <div className="neon-card p-3 text-center">
                          <div className="font-display font-bold text-[18px]" style={{ color: '#F2F4FF' }}>{listing.sqft.toLocaleString()}</div>
                          <div className="text-[11px]" style={{ color: '#A7A3C4' }}>Sqft</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Status notices */}
                  {listing.status === 'Pending' && (
                    <div className="px-4 py-3 rounded-lg" style={{ background: 'rgba(185,123,26,0.06)', border: '1px solid rgba(185,123,26,0.15)' }}>
                      <p className="text-[13px] font-medium" style={{ color: '#F0A832' }}>Sale pending for this unit.</p>
                    </div>
                  )}
                  {(listing.status === 'Sold' || listing.status === 'Leased') && (
                    <div className="px-4 py-3 rounded-lg" style={{ background: 'rgba(167,163,196,0.06)', border: '1px solid rgba(167,163,196,0.15)' }}>
                      <p className="text-[13px] font-medium" style={{ color: '#A7A3C4' }}>This unit is no longer available.</p>
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <h3 className="font-display font-semibold text-[14px] mb-2" style={{ color: '#F2F4FF' }}>Description</h3>
                    <p className="text-[14px] leading-relaxed" style={{ color: 'rgba(242,244,255,0.75)' }}>
                      {listing.description}
                    </p>
                  </div>

                  {/* Features */}
                  {listing.features && listing.features.length > 0 && (
                    <div>
                      <h3 className="font-display font-semibold text-[14px] mb-3" style={{ color: '#F2F4FF' }}>Features</h3>
                      <ul className="space-y-2">
                        {listing.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: color }} />
                            <span className="text-[13px]" style={{ color: '#A7A3C4' }}>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {listing.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[12px] px-2.5 py-1 rounded"
                        style={{ background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* CTA */}
                  {(listing.status === 'Active' || listing.status === 'Available') && (
                    <button className="btn-primary w-full text-[14px] py-3">
                      Express Interest
                    </button>
                  )}
                  <p className="text-[12px]" style={{ color: 'rgba(167,163,196,0.35)' }}>
                    Contact information not published here. Connect via the resident directory or community channels.
                  </p>
                </div>
              </div>

              {/* Related */}
              {relatedListings.length > 0 && (
                <div className="mt-14">
                  <h2 className="font-display font-semibold text-[18px] mb-5" style={{ color: '#F2F4FF' }}>
                    More in {listing.propertyType}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedListings.map((rel) => (
                      <RelatedListingCard key={rel.id} listing={rel} />
                    ))}
                  </div>
                </div>
              )}

              {/* Back CTA */}
              <div className="mt-10 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <Link href="/resources/real-estate" className="btn-secondary text-[14px] px-5 py-2.5 inline-flex items-center gap-2">
                  <ArrowLeft size={14} /> Back to Real Estate
                </Link>
              </div>
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
